import { formatEventDate } from "../utils/formatEventDate"
import MasonryCollection from "./components/MasonryCollection"
import { GalleryDesignProps } from "./types"
import PhotographerBar from "./components/PhotographerBar"
import CoverImage from "./components/CoverImage"

/**
 * 04 · Cinematic
 * Light + dark. A landing variant: a tall full-bleed cover with the title
 * overlaid on a soft bottom gradient (Bodoni italic display over Archivo UI).
 * Photos use the shared Masonry collection — only the first-page/cover treatment
 * differs from the other designs. Color mode comes from the body `dark` class.
 */
const CinematicGallery = ({
  data,
  folderBar,
  contentMode,
  aiContent,
  emptyContent,
  photos,
  onCoverLoad,
}: GalleryDesignProps) => {
  const name = data?.name || ""
  const dateLabel = formatEventDate(data?.startDate, data?.endDate, data?.date)
  const cover = data?.coverPhotoLink
  const vendor = data?.vendors?.[0]

  return (
    <div className="relative w-full bg-white text-[#151515] dark:bg-[#0b0b0b] dark:text-[#f0efec] font-archivo">
      {/* Photographer bar — sits over the cover image. */}
      <PhotographerBar vendor={vendor} tone="light" />

      {/* Cinematic cover with the title overlaid on a soft bottom gradient. */}
      <div className="relative w-full h-[70vh] md:h-[92vh] bg-[#e9e9e9] dark:bg-[#181818] overflow-hidden">
        {cover && (
          <CoverImage
            src={cover}
            alt={name}
            className="w-full h-full object-cover"
            onLoad={onCoverLoad}
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 flex flex-col items-center text-center px-6 pb-10 md:pb-16 text-white">
          <h1 className="font-bodoni italic font-light leading-[0.95] tracking-[0.01em] text-[38px] md:text-[66px] lg:text-[76px] drop-shadow-[0_2px_16px_rgba(0,0,0,0.45)] break-words">
            {name}
          </h1>
          {dateLabel && (
            <div className="mt-4 flex items-center justify-center gap-3 md:gap-4">
              <span className="h-px w-8 md:w-12 bg-white/60" />
              <span className="text-[11px] md:text-[12px] font-medium tracking-[0.34em] uppercase text-white/90">
                {dateLabel}
              </span>
              <span className="h-px w-8 md:w-12 bg-white/60" />
            </div>
          )}
        </div>
      </div>

      {/* Folder bar + collection share one tall parent so the sticky folder bar
          stays pinned while the photos scroll. */}
      <div className="flex flex-col items-center w-full">
        {folderBar}

        <div className="w-full px-4 md:px-14 pt-6 pb-10 min-h-[40vh]">
          {contentMode === "ai" ? (
            aiContent
          ) : contentMode === "photos" ? (
            <MasonryCollection photos={photos} columnsClass="columns-2 md:columns-3" />
          ) : (
            emptyContent
          )}
        </div>
      </div>
    </div>
  )
}

export default CinematicGallery
